import { useMemo, useState } from "react";

/** Onboarding wizard: hand-holds a DAO/validator through connecting their treasury.
 *  It generates the exact on-chain authz grant to run (from the treasury, or as a
 *  DAO governance proposal) - the non-custodial "connect" step - then points to
 *  Policies to set the matching rules. We never see or hold a private key. */
type ActionKey = "bank.send" | "staking.delegate" | "gov.vote" | "distribution.withdrawRewards";
const ACTIONS: { id: ActionKey; label: string }[] = [
  { id: "bank.send", label: "💸 Send tokens (payouts)" },
  { id: "staking.delegate", label: "🥩 Delegate / stake" },
  { id: "gov.vote", label: "🗳️ Vote in governance" },
  { id: "distribution.withdrawRewards", label: "🎁 Withdraw rewards" },
];
const STEPS = ["Your setup", "Authorize on-chain", "Set your rules", "Done"] as const;
const UATOM_PER_ATOM = 1_000_000;

function atomToUatom(atom: string): string | null {
  const t = atom.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * UATOM_PER_ATOM).toString();
}

export function Connect() {
  const [step, setStep] = useState(0);
  const [operator, setOperator] = useState("");
  const [treasury, setTreasury] = useState("");
  const [chainId, setChainId] = useState("cosmoshub-4");
  const [action, setAction] = useState<ActionKey>("bank.send");
  const [amount, setAmount] = useState("");
  const [target, setTarget] = useState("");

  const grant = useMemo(() => {
    const op = operator.trim() || "<operator-address>";
    const from = `--from ${treasury.trim() || "<treasury-key>"} --chain-id ${chainId.trim() || "<chain-id>"} --gas auto --gas-adjustment 1.4 --fees 5000uatom -y`;
    const ua = atomToUatom(amount);
    if (action === "bank.send") return `gaiad tx authz grant ${op} send --spend-limit ${ua ?? "<amount>"}uatom ${from}`;
    if (action === "staking.delegate") return `gaiad tx authz grant ${op} delegate${target.trim() ? ` --allowed-validators ${target.trim()}` : ""} ${from}`;
    if (action === "gov.vote") return `gaiad tx authz grant ${op} generic --msg-type /cosmos.gov.v1.MsgVote ${from}`;
    return `gaiad tx authz grant ${op} generic --msg-type /cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward ${from}`;
  }, [operator, treasury, chainId, action, amount, target]);

  const revoke = useMemo(() => {
    const op = operator.trim() || "<operator-address>";
    const msg =
      action === "bank.send"
        ? "/cosmos.bank.v1beta1.MsgSend"
        : action === "staking.delegate"
          ? "/cosmos.staking.v1beta1.MsgDelegate"
          : action === "gov.vote"
            ? "/cosmos.gov.v1.MsgVote"
            : "/cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward";
    return `gaiad tx authz revoke ${op} ${msg} --from ${treasury.trim() || "<treasury-key>"} --chain-id ${chainId.trim() || "<chain-id>"} --fees 5000uatom -y`;
  }, [operator, treasury, chainId, action]);

  const hasAmount = action === "bank.send" || action === "staking.delegate";

  return (
    <>
      <h2>Connect your treasury</h2>

      <div className="card">
        <h3>4 steps - you keep your keys the whole time</h3>
        <p className="muted small">
          You authorize an operator to act on your behalf with ONE on-chain grant (scoped + revocable). Praxis never sees a private key. Then you set the rules.
        </p>

        <div className="steps">
          {STEPS.map((label, i) => (
            <button key={label} type="button" className={`step${i === step ? " active" : ""}${i < step ? " done" : ""}`} onClick={() => setStep(i)}>
              <span className="n">{i < step ? "✓" : i + 1}</span>
              {label}
            </button>
          ))}
        </div>

        {step === 0 && (
          <>
            <label>
              Your operator address <span className="muted">(from your Praxis onboarding - the key that will act for you)</span>
              <input value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="cosmos1… (your dedicated operator)" />
            </label>
            <div className="row">
              <label>
                Treasury key / account
                <input value={treasury} onChange={(e) => setTreasury(e.target.value)} placeholder="your gaiad key name or x/group account" />
              </label>
              <label>
                Network (chain id)
                <input value={chainId} onChange={(e) => setChainId(e.target.value)} placeholder="cosmoshub-4" />
              </label>
            </div>
            <label>
              What do you want to authorize?
              <select value={action} onChange={(e) => setAction(e.target.value as ActionKey)}>
                {ACTIONS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            {hasAmount && (
              <div className="row">
                <label>
                  Total spend limit (ATOM)
                  <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 100" inputMode="decimal" />
                </label>
                {action === "staking.delegate" && (
                  <label>
                    Allowed validator (optional)
                    <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="cosmosvaloper1…" />
                  </label>
                )}
              </div>
            )}
            <p className="hint">
              {hasAmount && atomToUatom(amount) ? `= ${atomToUatom(amount)} uatom · ` : ""}
              This is the grant&rsquo;s TOTAL on-chain ceiling - a cumulative budget that decrements as it&rsquo;s spent (no daily reset). Per-day / per-hour limits are set
              separately in your <a href="#/policies">policy</a> (rolling cap: 24h = per day, 1h = per hour).
            </p>
          </>
        )}

        {step === 1 && (
          <>
            <h3>Authorize on-chain (one grant)</h3>
            <p className="muted small">
              Run this from your treasury key - or, for a DAO, submit it as a governance proposal (your x/group / DAO executes the <code>MsgGrant</code>). This is the only time your key signs.
            </p>
            <div className="code-head">
              <span>Grant command</span>
              <button type="button" className="ghost" onClick={() => void navigator.clipboard?.writeText(grant)}>
                Copy
              </button>
            </div>
            <pre>{grant}</pre>
            <p className="hint">Scoped to exactly this action and limit. Praxis can never exceed it - the blockchain enforces the ceiling.</p>
            <details style={{ marginTop: 8 }}>
              <summary className="muted small">Revoke later (kill-switch)</summary>
              <pre>{revoke}</pre>
              <p className="muted small">Run this (or pass it through governance) any time to instantly cut the operator off.</p>
            </details>
          </>
        )}

        {step === 2 && (
          <>
            <h3>Set your rules</h3>
            <p>
              The on-chain grant is the hard ceiling. Now add your <b>policy</b> - the same or tighter limits, plus allow-lists, approvals, schedules. Go to{" "}
              <a href="#/policies">Policies</a> and pick a template or build your own.
            </p>
            <p className="hint">Tip: mirror the action you just authorized, and set the same (or lower) amount as the grant. Big actions can require approval from your group.</p>
            <a className="btn-cta" href="#/policies">
              Open the policy builder →
            </a>
          </>
        )}

        {step === 3 && (
          <>
            <h3>You’re connected 🎉</h3>
            <div className="summary">
              <ul>
                <li className="yes">Your treasury authorized the operator on-chain (scoped + revocable).</li>
                <li className="yes">Your policy decides what actually runs - within the grant.</li>
                <li className="yes">Every action is logged and anchored on-chain (see Audit).</li>
                <li className="no">We never held a private key - yours stays with you; the operator key lives in a KMS/HSM.</li>
              </ul>
            </div>
            <p className="hint">
              Next: submit an action in <a href="#/intents">Intents</a>, or set up a recurring schedule. Revoke the grant any time (step 2 → Revoke).
            </p>
          </>
        )}

        <div className="wizard-nav">
          <button type="button" className="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            ← Back
          </button>
          {step < STEPS.length - 1 && (
            <button type="button" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
              Next →
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <h3>How key custody works (so you trust it)</h3>
        <p className="muted small">
          Two keys, two owners. <b>Your treasury key</b> never leaves you - it only signs the one grant (and revokes). The <b>operator key</b> Praxis uses lives in a KMS/HSM and can only{" "}
          <code>MsgExec</code> within your grant - it never holds your funds. Even if Praxis were fully compromised, no one can exceed your on-chain grant or your policy.
        </p>
      </div>
    </>
  );
}
